import { type EpbGrade } from './indexationUtils';
import { convertNumberToHexByte } from './numbers';
import { ContactType } from './types';

export enum Colors {
  TRANSPARENT = '#00000000',
  CLASSICAL_WHITE = '#FFFFFF',
  PORCELAIN_GREY_1 = '#F5F7F8', // grey-05
  PORCELAIN_GREY_2 = '#ECF0F1',
  PORCELAIN_GREY_3 = '#ECEFF1', // grey-10
  GEYSER_GREY = '#DDE3E6',
  SILVER = '#CFD8DC',
  SEPARATOR_GREY = '#D0D8DC',
  TOWER_GREY = '#AFBDC4', // grey-25
  NOBEL_GREY = '#B3B3B3',
  ROLLING_STONE_GREY = '#757778',
  BLUEY = '#90A4AE',
  LIGHT_BLUE_GREY = '#78909C', // grey-40
  BLUE_GREY = '#607D8B',
  SLATE_GREY = '#546E7A',
  DARK_SLATE_GREY = '#455A64',
  CHARCOAL_GREY = '#37474F',
  DARK = '#263238',
  CLASSICAL_BLACK = '#000000',
  CARNATION_RED = '#FC5C65',
  FADED_RED = '#FC5C6526',
  STRONG_PINK = '#FF0073',
  STRAWBERRY_PINK = '#FF3399',
  STRIPE_PURPLE = '#635BFF',
  LIGHT_PURPLE = '#A55EEA',
  ELECTRIC_VIOLET = '#9933FF',
  VIVID_VIOLET = '#8E45A1',
  DARK_BLUE = '#1138B1',
  CHAMBRAY_BLUE = '#3b5998',
  RIBBON_BLUE = '#3355FF',
  ROYAL_BLUE = '#4B66EC',
  DODGER_BLUE = '#4B7BEC',
  DARK_SKY_BLUE = '#45AAF2',
  CYAN_BLUE = '#33DDFF',
  TURQUOISE_BLUE = '#2BCBBA',
  AQUAMARINE_BLUE = '#6AE1CD',
  MINTY_GREEN = '#26DE81',
  MALACHITE_GREEN = '#00C851',
  HAZE_GREEN = '#009C4D',
  PADUA_GREEN = '#B1E9BB',
  SCREAMING_GREEN = '#55FF33',
  FIZZ_YELLOW = '#FFFF33',
  SUN_YELLOW = '#FED330',
  BEIGE = '#E5D8A5',
  BEAVER_BROWN = '#91735A',
  CARROT_ORANGE = '#FD9644',
  BURNING_ORANGE = '#FF7033',
  SCARLET_ORANGE = '#FF3500',
  RED_ORANGE = '#FF3333',
  BLACK_20OPACITY = '#00000033',
  BLACK_26OPACITY = '#00000042',
  YELLOW_WARNING = '#FED13026',
  MUI_BORDER_GREY = '#E0E0E0',
  EPB_GRADE_A_PLUS_PLUS = '#00ADEF',
  EPB_GRADE_A_PLUS = '#228B37',
  EPB_GRADE_A = '#30B54D',
  EPB_GRADE_B = '#64C544',
  EPB_GRADE_C = '#BADE26',
  EPB_GRADE_D = '#DBE821',
  EPB_GRADE_E = '#FBC111',
  EPB_GRADE_F = '#F78F27',
  EPB_GRADE_G = '#ED1C24',
}

export const EPB_GRADE_COLORS: Record<EpbGrade, Colors> = {
  'A++': Colors.EPB_GRADE_A_PLUS_PLUS,
  'A+': Colors.EPB_GRADE_A_PLUS,
  A: Colors.EPB_GRADE_A,
  B: Colors.EPB_GRADE_B,
  C: Colors.EPB_GRADE_C,
  D: Colors.EPB_GRADE_D,
  E: Colors.EPB_GRADE_E,
  F: Colors.EPB_GRADE_F,
  G: Colors.EPB_GRADE_G,
};

export const CONTACT_TYPES_COLORS: Record<ContactType, Colors> = {
  [ContactType.OWNER]: Colors.DARK_BLUE,
  [ContactType.TENANT]: Colors.MALACHITE_GREEN,
  [ContactType.GUARANTOR]: Colors.BURNING_ORANGE,
  [ContactType.MEMBER]: Colors.LIGHT_PURPLE,
  [ContactType.CONTRACTOR]: Colors.BEAVER_BROWN,
  [ContactType.JOINT_OWNERSHIP]: Colors.VIVID_VIOLET,
  [ContactType.CLIENT]: Colors.DODGER_BLUE,
  [ContactType.HOA]: Colors.SLATE_GREY,
  [ContactType.OTHER]: Colors.ROLLING_STONE_GREY,
};

export const getColorWithOpacity = (color: Colors, opacity: number) => `${color.slice(0, 7)}${opacity}`;

export const getBorderColorWithOpacity = (color: Colors | string, opacity: number) =>
  `${color.slice(0, 7)}${Math.round(opacity * 255)
    .toString(16)
    .padStart(2, '0')}`;

export const getDarkerColor = (color: string, darkFactor = 0.8) => {
  const red = Math.round(parseInt(color.slice(1, 3), 16) * darkFactor);
  const green = Math.round(parseInt(color.slice(3, 5), 16) * darkFactor);
  const blue = Math.round(parseInt(color.slice(5, 7), 16) * darkFactor);
  return `#${convertNumberToHexByte(red)}${convertNumberToHexByte(green)}${convertNumberToHexByte(blue)}`;
};

export const getContactTypeColor = (contactType: ContactType) => CONTACT_TYPES_COLORS[contactType];
